/**
 * Interlinked Guardian plan catalog (Product & Pricing Structure v1.0, Apr 2026).
 *
 * Annual list price = monthly × 11.5 (catalog / DB). Freemium has no trial.
 * At checkout on a paid personal plan, a 1-week trial bills monthly × 11.75.
 */

/** Months charged on annual billing (no trial). */
export const ANNUAL_MONTHS_CHARGED = 11.5;

/** Months charged on annual billing after a 1-week checkout trial. */
export const ANNUAL_TRIAL_MONTHS_CHARGED = 11.75;

/**
 * @param {number} monthlyCents
 * @returns {number}
 */
export function annualCentsFromMonthly(monthlyCents) {
  return Math.round(monthlyCents * ANNUAL_MONTHS_CHARGED);
}

/**
 * @param {number} monthlyCents
 * @returns {number}
 */
export function annualTrialCentsFromMonthly(monthlyCents) {
  return Math.round(monthlyCents * ANNUAL_TRIAL_MONTHS_CHARGED);
}

/**
 * @typedef {object} GuardianPlan
 * @property {string} id
 * @property {string} name
 * @property {string} blurb
 * @property {string} description
 * @property {string} target
 * @property {number|null} priceMonthlyCents - null = custom
 * @property {number|null} priceAnnualCents - null = custom / free (0 monthly)
 * @property {string|null} priceNote
 * @property {string[]} features
 * @property {boolean} popular
 * @property {'personal'|'team'} audience
 * @property {'try'|'contact'} ctaKind
 * @property {string} ctaLabel
 */

/** @type {GuardianPlan[]} */
export const GUARDIAN_PLANS = [
  {
    id: 'freemium',
    name: 'Freemium',
    blurb: 'Public access',
    description: 'Live map, basic risk bands, and limited chat for public awareness.',
    target: 'User acquisition & public awareness',
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    priceNote: null,
    features: [
      'Live wildfire map with active incidents',
      'Basic Risk of Ignition bands (low / medium / high)',
      'Read-only incident list',
      'Basic AI chat (limited, informational)',
      'Search, filtering, and community reporting',
    ],
    popular: false,
    audience: 'personal',
    ctaKind: 'try',
    ctaLabel: 'Get started free',
  },
  {
    id: 'citizen',
    name: 'Citizen',
    blurb: 'Local visibility',
    description: 'Higher-resolution risk maps and expanded chat for local monitoring.',
    target: 'General users, enthusiasts, local residents',
    priceMonthlyCents: 1199,
    priceAnnualCents: annualCentsFromMonthly(1199),
    priceNote: null,
    features: [
      'Everything in Freemium',
      'Higher-resolution risk maps',
      'More frequent data refreshes',
      'Expanded AI chat usage',
      'Basic historical fire data',
    ],
    popular: false,
    audience: 'personal',
    ctaKind: 'try',
    ctaLabel: 'Choose Citizen',
  },
  {
    id: 'researcher',
    name: 'Researcher',
    blurb: 'Analysis & export',
    description: 'Exports, extended history, and custom visualizations for analysis work.',
    target: 'Students, NGOs, analysts, researchers',
    priceMonthlyCents: 2499,
    priceAnnualCents: annualCentsFromMonthly(2499),
    priceNote: null,
    features: [
      'Everything in Citizen',
      'CSV export and limited API access',
      'Extended historical datasets',
      'Custom visualization tools',
      'Higher query limits',
    ],
    popular: false,
    audience: 'personal',
    ctaKind: 'try',
    ctaLabel: 'Choose Researcher',
  },
  {
    id: 'pro',
    name: 'Pro',
    blurb: 'Actionable intelligence',
    description: 'Full RoI scores, tactical AI guidance, and regional monitoring.',
    target: 'Advanced users, consultants, small teams',
    priceMonthlyCents: 7499,
    priceAnnualCents: annualCentsFromMonthly(7499),
    priceNote: null,
    features: [
      'Everything in Researcher',
      'Full Risk of Ignition score, drivers, and confidence',
      'Advanced AI agent with tactical insights',
      'Higher API limits',
      'Real-time monitoring for limited regions / zones',
    ],
    popular: true,
    audience: 'personal',
    ctaKind: 'try',
    ctaLabel: 'Choose Pro',
  },
  {
    id: 'organization',
    name: 'Organization',
    blurb: 'Team collaboration',
    description: 'Shared dashboards, team alerts, and multi-user collaboration.',
    target: 'Small teams, local agencies, startups',
    priceMonthlyCents: 4999,
    priceAnnualCents: annualCentsFromMonthly(4999),
    priceNote: 'Per seat · first 3 seats; $99.99/seat after',
    features: [
      'Everything in Pro',
      'Multi-user collaboration',
      'Shared dashboards and data views',
      'Team-level alerts and monitoring',
      'Centralized access to regions and zones',
    ],
    popular: false,
    audience: 'team',
    ctaKind: 'try',
    ctaLabel: 'Choose Organization',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    blurb: 'Customized for you',
    description: 'Large-scale territories, integrations, dedicated support, and SLAs.',
    target: 'Utilities, insurers, governments, large agencies',
    priceMonthlyCents: null,
    priceAnnualCents: null,
    priceNote: 'Custom invoice-based pricing',
    features: [
      'Full platform access across all components',
      'High-scale polygon / territory ingestion',
      'Full API access and GIS / dispatch integrations',
      'Custom data pipelines and region-tuned RoI',
      'Dedicated support and SLAs',
    ],
    popular: false,
    audience: 'team',
    ctaKind: 'contact',
    ctaLabel: 'Contact us',
  },
];

/** Personal plans shown in the primary card grid (matches platform billing panel). */
export const GUARDIAN_PERSONAL_PLANS = GUARDIAN_PLANS.filter((p) => p.audience === 'personal');

/** Organization + Enterprise. */
export const GUARDIAN_TEAM_PLANS = GUARDIAN_PLANS.filter((p) => p.audience === 'team');

const TEAM_TIERS = new Set(['organization', 'enterprise', 'org']);

/** UI-only overlays when mapping DB/API plan rows. */
const PLAN_UI_META = {
  freemium: { ctaLabel: 'Get started free', popular: false },
  citizen: { ctaLabel: 'Choose Citizen', popular: false },
  researcher: { ctaLabel: 'Choose Researcher', popular: false },
  pro: { ctaLabel: 'Choose Pro', popular: true },
  organization: {
    ctaLabel: 'Choose Organization',
    popular: false,
    priceNote: 'Per seat · first 3 seats; $99.99/seat after',
  },
  enterprise: { ctaLabel: 'Contact us', popular: false, ctaKind: 'contact' },
};

/**
 * Normalize API/DB cents: -1 means custom / unset.
 * @param {number|null|undefined} cents
 * @returns {number|null}
 */
export function normalizePriceCents(cents) {
  if (cents == null || cents < 0) return null;
  return cents;
}

/**
 * Map GET /guardian/plans (or /billing/plans) rows into card catalog shape.
 * Falls back to hardcoded GUARDIAN_PLANS when the API returns nothing.
 * @param {Array<object>|null|undefined} apiPlans
 * @returns {typeof GUARDIAN_PLANS}
 */
export function mapBillingPlansToGuardianPlans(apiPlans) {
  if (!Array.isArray(apiPlans) || apiPlans.length === 0) {
    return GUARDIAN_PLANS;
  }

  return apiPlans
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((row) => {
      const slug = row.slug || row.tier || row.id;
      const meta = PLAN_UI_META[slug] || {};
      const fallback = GUARDIAN_PLANS.find((p) => p.id === slug);
      const monthly = normalizePriceCents(row.priceMonthlyCents);
      let annual = normalizePriceCents(row.priceAnnualCents);
      if (annual == null && monthly != null && monthly > 0) {
        annual = annualCentsFromMonthly(monthly);
      }
      if (monthly === 0) annual = 0;

      return {
        id: slug,
        name: row.name || fallback?.name || slug,
        blurb: fallback?.blurb || '',
        description: row.description || fallback?.description || '',
        target: fallback?.target || '',
        priceMonthlyCents: monthly,
        priceAnnualCents: annual,
        priceNote: meta.priceNote ?? fallback?.priceNote ?? null,
        features: Array.isArray(row.features) && row.features.length
          ? row.features
          : (fallback?.features || []),
        popular: Boolean(meta.popular ?? fallback?.popular),
        audience: TEAM_TIERS.has((row.tier || slug || '').toLowerCase()) ? 'team' : 'personal',
        ctaKind: meta.ctaKind || fallback?.ctaKind || 'try',
        ctaLabel: meta.ctaLabel || fallback?.ctaLabel || `Choose ${row.name || slug}`,
      };
    });
}

/**
 * @param {number|null} monthlyCents
 * @param {number|null} annualCents
 * @param {'monthly'|'annual'} billing
 * @returns {number|null}
 */
export function displayPeriodCents(monthlyCents, annualCents, billing) {
  const monthly = normalizePriceCents(monthlyCents);
  const annual = normalizePriceCents(annualCents);
  if (monthly == null) return null;
  if (monthly === 0) return 0;
  if (billing === 'annual') return annual ?? annualCentsFromMonthly(monthly);
  return monthly;
}

/**
 * @param {number} cents
 * @returns {string}
 */
export function formatUsdFromCents(cents) {
  const dollars = cents / 100;
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}

/**
 * Map a catalog slug or legacy GuardianUser.tier onto a plan id.
 * @param {string|null|undefined} raw
 */
export function planIdFromEntitlementTier(raw) {
  const t = (raw || '').toLowerCase();
  if (t === 'premium' || t === 'pro') return 'pro';
  if (t === 'enterprise') return 'enterprise';
  if (t === 'organization' || t === 'org') return 'organization';
  if (t === 'researcher') return 'researcher';
  if (t === 'citizen') return 'citizen';
  if (t === 'freemium' || t === 'free') return 'freemium';
  return 'freemium';
}

/**
 * Current catalog plan. Prefer plan_slug (billing subscription / trial)
 * over legacy entitlements.tier (free / premium).
 * @param {object|null|undefined} entitlements
 * @param {string|null|undefined} [fallbackTier]
 */
export function planIdFromEntitlements(entitlements, fallbackTier) {
  return planIdFromEntitlementTier(
    entitlements?.plan_slug || entitlements?.tier || fallbackTier,
  );
}
